import type { AdvanceCategory, AdvanceTransaction } from '../../services/eeapService'
import { ANNUAL_ALLOWANCE, remainingAllowancesByEmployee } from '../../utils/advanceAllowance.ts'
import { sortAdvanceRows, type DateSort, type LedgerSortField } from '../../utils/advanceRowSort.ts'
import {
  ledgerCategoryLabel,
  mergeLedgerCategoryOptions,
  type AdvanceCategoryDef,
} from '../../utils/advanceCategories.ts'

export type { DateSort, LedgerSortField, AdvanceCategoryDef }

export type LedgerView = 'journal' | 'balances'

export type CategoryFilter = 'all' | AdvanceCategory

export type ItemCategoryFilter = 'all' | 'none' | string

export type LedgerFilters = {
  queries: string[]
  dateFrom: string
  dateTo: string
  category: CategoryFilter
  itemCategory?: ItemCategoryFilter
  dateSort?: DateSort
  sortField?: LedgerSortField
  includeScheduled?: boolean
  excludeDeductions?: boolean
  deductionsOnly?: boolean
  employeeId?: string
  categoryCatalog?: AdvanceCategoryDef[]
  hireDates?: Record<string, string | null | undefined>
}

export type PeriodTotals = {
  amount: number
  employer: number
  employee: number
}

export type EmployeeBalanceRow = {
  employeeId: string
  employeeName: string
  byCategory: Record<string, number>
  eeap: number
  eeapAdvance: number
  advance: number
  total: number
  remainingAllowance: number
  lastActivity: string | null
}

export function asLedgerCategory(raw: string | undefined | null): AdvanceCategory {
  return String(raw || '').trim() || 'eeap'
}

export function categoryLabel(category: AdvanceCategory | string, catalog?: AdvanceCategoryDef[] | null) {
  return ledgerCategoryLabel(category, catalog)
}

export function splitCategoryKeys(catalog: AdvanceCategoryDef[], rows: EmployeeBalanceRow[]): string[] {
  return mergeLedgerCategoryOptions(
    catalog,
    rows.flatMap((row) => Object.keys(row.byCategory || {})),
  ).map((item) => item.key)
}

export function rowDay(iso: string | null | undefined) {
  if (!iso) return ''
  const match = String(iso).match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

/** Calendar day for display. Ledger dates are UTC midnight, so local toLocaleDateString() is a day early. */
export function formatLedgerDate(iso: string | null | undefined) {
  const day = rowDay(iso)
  if (!day) return '—'
  const [year, month, date] = day.split('-').map(Number)
  return new Date(year, month - 1, date).toLocaleDateString()
}

export function inPeriod(day: string, dateFrom: string, dateTo: string) {
  if (dateFrom && day && day < dateFrom) return false
  if (dateTo && day && day > dateTo) return false
  return true
}

export function asOfDate(day: string, dateTo: string) {
  if (dateTo && day && day > dateTo) return false
  return true
}

export function matchesCategory(row: { category?: string }, category: CategoryFilter) {
  if (category === 'all') return true
  return row.category === category
}

export function matchesItemCategory(row: { itemCategory?: string }, filter?: ItemCategoryFilter) {
  if (!filter || filter === 'all') return true
  const value = String(row.itemCategory || '').trim()
  if (filter === 'none') return !value
  return value === filter
}

export function matchesEmployeeId(row: { employeeId?: string }, employeeId?: string) {
  if (!employeeId) return true
  return row.employeeId === employeeId
}

export function ledgerDraftHasValue(opts: {
  amount: number
  employer: number
  employee: number
  balance: number | null
}) {
  const values = [opts.amount, opts.employer, opts.employee]
  if (opts.balance != null && Number.isFinite(opts.balance)) values.push(opts.balance)
  return values.some((n) => Number.isFinite(n) && n !== 0)
}

export function roundMoney(n: number | undefined | null) {
  const value = Number(n ?? 0)
  if (!Number.isFinite(value)) return 0
  return Math.round(value * 100) / 100
}

export function formatUsd(n: number | undefined | null) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(roundMoney(n))
}

export function moneyValueClass(n: number | undefined | null, opts?: { positiveGreen?: boolean }) {
  const value = Number(n ?? 0)
  if (value < 0) return 'text-error'
  if (opts?.positiveGreen && value > 0) return 'text-success'
  return undefined
}

export function haystack(row: AdvanceTransaction, catalog?: AdvanceCategoryDef[] | null) {
  const day = rowDay(row.date)
  return [
    row.employeeName,
    row.employeeId,
    row.categoryLabel,
    categoryLabel(row.category, catalog),
    row.category,
    row.itemCategory,
    row.description,
    formatUsd(row.amount),
    formatUsd(row.employerContribution),
    formatUsd(row.employeeAmount),
    day ? formatLedgerDate(row.date) : '',
    day,
    row.scheduled ? 'scheduled' : '',
  ]
    .map((v) => String(v || '').toLowerCase())
    .join(' ')
}

export function searchQueries(search: string, pinned: string[]) {
  const queries = [...pinned]
  if (search.trim()) queries.push(search.trim())
  return queries
}

export function matchesQueries(hay: string, queries: string[]) {
  if (!queries.length) return true
  return queries.every((query) => {
    const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean)
    if (!tokens.length) return true
    return tokens.every((token) => hay.includes(token))
  })
}

export function isDeductionRow(row: AdvanceTransaction) {
  return Number(row.amount || 0) < 0 || Number(row.employeeAmount || 0) < 0
}

export function isSingleEmployeeJournal(rows: { employeeId?: string }[]) {
  const ids = new Set(rows.map((row) => String(row.employeeId || '')).filter(Boolean))
  return ids.size === 1
}

export function periodTotals(rows: AdvanceTransaction[]): PeriodTotals {
  return rows.reduce(
    (acc, row) => ({
      amount: acc.amount + Number(row.amount || 0),
      employer: acc.employer + Number(row.employerContribution || 0),
      employee: acc.employee + Number(row.employeeAmount || 0),
    }),
    { amount: 0, employer: 0, employee: 0 },
  )
}

export function filterJournalRows(rows: AdvanceTransaction[], filters: LedgerFilters) {
  const matched = rows.filter((row) => {
    if (!filters.includeScheduled && row.scheduled) return false
    if (filters.excludeDeductions && isDeductionRow(row)) return false
    if (filters.deductionsOnly && !isDeductionRow(row)) return false
    if (!matchesCategory(row, filters.category)) return false
    if (!matchesItemCategory(row, filters.itemCategory)) return false
    if (!matchesEmployeeId(row, filters.employeeId)) return false
    if (!inPeriod(rowDay(row.date), filters.dateFrom, filters.dateTo)) return false
    if (!matchesQueries(haystack(row, filters.categoryCatalog), filters.queries)) return false
    return true
  })
  const labeled = matched.map((row) => ({
    ...row,
    categoryLabel: row.categoryLabel || categoryLabel(row.category, filters.categoryCatalog),
  }))
  return sortAdvanceRows(labeled, filters.dateSort || 'asc', filters.sortField || 'date')
}

/** Rows that participate in journal running balance (category + scheduled, not date/search). */
export function rowsForJournalRunning<T extends { scheduled?: boolean; category?: string }>(
  rows: T[],
  filters: Pick<LedgerFilters, 'category' | 'includeScheduled'>,
): T[] {
  return rows.filter((row) => {
    if (!filters.includeScheduled && row.scheduled) return false
    if (!matchesCategory(row, filters.category)) return false
    return true
  })
}

export function journalBalanceTitle(category: CategoryFilter, catalog?: AdvanceCategoryDef[] | null) {
  if (category === 'all') return "This employee's outstanding after this line (all categories)"
  return `This employee's ${categoryLabel(category, catalog)} outstanding after this line`
}

export function employeeBalances(rows: AdvanceTransaction[], filters: LedgerFilters): EmployeeBalanceRow[] {
  const period = rows.filter((row) => {
    if (row.scheduled) return false
    if (!matchesCategory(row, filters.category)) return false
    if (!matchesItemCategory(row, filters.itemCategory)) return false
    if (!matchesEmployeeId(row, filters.employeeId)) return false
    if (!inPeriod(rowDay(row.date), filters.dateFrom, filters.dateTo)) return false
    if (!matchesQueries(haystack(row, filters.categoryCatalog), filters.queries)) return false
    return true
  })
  const ids = new Set(period.map((row) => row.employeeId).filter(Boolean))
  const byId = new Map<string, EmployeeBalanceRow>()

  for (const row of rows) {
    if (row.scheduled) continue
    if (!ids.has(row.employeeId)) continue
    if (!matchesCategory(row, filters.category)) continue
    if (!asOfDate(rowDay(row.date), filters.dateTo)) continue
    const current = byId.get(row.employeeId) || {
      employeeId: row.employeeId,
      employeeName: row.employeeName || row.employeeId,
      byCategory: {},
      eeap: 0,
      eeapAdvance: 0,
      advance: 0,
      total: 0,
      remainingAllowance: ANNUAL_ALLOWANCE,
      lastActivity: null as string | null,
    }
    if (row.employeeName && current.employeeName === current.employeeId) {
      current.employeeName = row.employeeName
    }
    const amount = Number(row.employeeAmount || 0)
    const key = String(row.category || '').trim() || 'eeap'
    current.byCategory = { ...current.byCategory, [key]: (current.byCategory[key] || 0) + amount }
    current.eeap = current.byCategory.eeap ?? 0
    current.eeapAdvance = current.byCategory.eeapAdvance ?? 0
    current.advance = current.byCategory.advance ?? 0
    current.total += amount
    const day = rowDay(row.date)
    if (day && (!current.lastActivity || day > current.lastActivity)) current.lastActivity = day
    byId.set(row.employeeId, current)
  }

  const remainingById = remainingAllowancesByEmployee(
    rows,
    filters.hireDates || {},
    filters.dateTo || '',
    ids,
  )
  for (const row of byId.values()) {
    row.remainingAllowance = remainingById.get(row.employeeId) ?? ANNUAL_ALLOWANCE
  }

  return [...byId.values()].sort((a, b) => {
    const name = String(a.employeeName || '').localeCompare(String(b.employeeName || ''))
    if (name !== 0) return name
    return a.employeeId.localeCompare(b.employeeId)
  })
}
